import axiosInstance from "../lib/axios.instance";
import {
  IUserSignIn,
  ILoginResponse,
  IUser,
  IForgotPasswordRequest,
  IForgotPasswordResponse,
  IChangePasswordRequest,
  IChangePasswordResponse,
  IResetPasswordInput,
  IResetPasswordOutput,
  IVerify2faRequest,
  IVerify2faResponse,
  IVerifyBackupCodeRequest,
  IVerifyBackupCodeResponse,
} from "../types/auth.types";

// Login function - only uses auth/sign-in endpoint
export const login = async (data: IUserSignIn): Promise<ILoginResponse> => {
  const { is_addon, username, password } = data;
  const body = new URLSearchParams({ username, password });
  const response = await axiosInstance.post<ILoginResponse>("auth/sign-in", body, {
    params: {
      is_addon,
    },
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });

  return response.data;
};

// Get user function
export const getUser = async (): Promise<IUser> => {
  const response = await axiosInstance.get<IUser>("auth/user");
  return response.data;
};

// Send reset password email (Step 1 of forgot password flow)
export const sendResetPasswordEmail = async (
  data: IForgotPasswordRequest
): Promise<IForgotPasswordResponse> => {
  const response = await axiosInstance.post<IForgotPasswordResponse>("auth/forgot-password", data, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

// Reset password with token (Step 3 of forgot password flow - used via web link)
export const resetPasswordWithToken = async (
  data: IResetPasswordInput
): Promise<IResetPasswordOutput> => {
  const response = await axiosInstance.put<IResetPasswordOutput>("auth/reset-password", data, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

// Change password (for authenticated users who know their old password)
export const changePassword = async (
  data: IChangePasswordRequest
): Promise<IChangePasswordResponse> => {
  const response = await axiosInstance.post<IChangePasswordResponse>("auth/change-password", data, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

// Verify a TOTP authenticator code against the pending-2FA token.
// The pending token is passed explicitly so it stays out of localStorage
// and the axios interceptor — backend rejects it on any non-2FA endpoint.
export const verify2fa = async (
  data: IVerify2faRequest,
  pendingAccessToken: string
): Promise<IVerify2faResponse> => {
  const response = await axiosInstance.post<IVerify2faResponse>("auth/2mfa/verify", data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${pendingAccessToken}`,
    },
  });
  return response.data;
};

// Verify a 2FA backup code against the pending-2FA token.
export const verifyBackupCode = async (
  data: IVerifyBackupCodeRequest,
  pendingAccessToken: string
): Promise<IVerifyBackupCodeResponse> => {
  const response = await axiosInstance.post<IVerifyBackupCodeResponse>(
    "auth/2mfa/verify-backup-code",
    data,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${pendingAccessToken}`,
      },
    }
  );
  return response.data;
};

// refreshToken is in auth-refresh.service.ts to avoid a circular dependency
// with axios.instance.ts. Re-export it for backward compatibility.
export { refreshToken } from "./auth-refresh.service";
